import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

type ServiceItem = {
  id: number;
  title: string;
  description: string;
  image: string;
  imagePosition?: string;
};

// ── Mobile carousel constants (same pattern as ConstructionSection) ────────────
const MOBILE_COL_W = 155;
const MOBILE_CARD_H = 190;
const MOBILE_GAP = 16;
const MOBILE_STEP = MOBILE_COL_W + MOBILE_GAP;
const MOBILE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const MOBILE_DURATION = "520ms";

const DIST_STYLE: Record<number, { opacity: number; scale: number }> = {
  0: { opacity: 1, scale: 1 },
  1: { opacity: 0.45, scale: 0.93 },
  2: { opacity: 0.15, scale: 0.86 },
};

const distStyle = (dist: number) => DIST_STYLE[Math.min(dist, 2)];

function useIsMobile(breakpointPx = 768) {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpointPx;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpointPx);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isMobile;
}

const ServicesView: React.FC = () => {
  const isMobile = useIsMobile(768);
  const sectionRef = useRef<HTMLElement | null>(null);

  const serviceGroups: ServiceItem[][] = useMemo(
    () => [
      [
        {
          id: 1,
          title: "Desarrollo Web",
          description:
            "Creamos aplicaciones web modernas y responsive con las últimas tecnologías.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743200/webservice_dkiums.webp",
          imagePosition: "20% center",
        },
        {
          id: 2,
          title: "Aplicaciones Móviles",
          description:
            "Desarrollamos aplicaciones nativas e híbridas para iOS y Android.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743190/appservice_m2xn3r.webp",
        },
        {
          id: 3,
          title: "Diseño UX/UI",
          description:
            "Diseñamos interfaces intuitivas y experiencias de usuario excepcionales.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743198/uiservice_yvnlpm.webp",
          imagePosition: "79% center",
        },
      ],
      [
        {
          id: 4,
          title: "Tiendas Electrónicas",
          description:
            "Soluciones completas de comercio electrónico para tu negocio.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743197/shopservice_wqme0o.webp",
          imagePosition: "60% center",
        },
        {
          id: 5,
          title: "Soluciones en la Nube",
          description:
            "Implementamos infraestructuras cloud escalables y seguras.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743193/cloudservice_vz3tgn.webp",
          imagePosition: "30% center",
        },
        {
          id: 6,
          title: "Análisis de datos",
          description:
            "Transformamos tus datos en insights accionables para tu negocio.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743194/dataservice_ydulsh.webp",
          imagePosition: "55% center",
        },
      ],
      [
        {
          id: 7,
          title: "Marketing Digital",
          description:
            "Estrategias digitales para aumentar tu presencia online.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743197/marketingservice_szvuwo.webp",
          imagePosition: "60% center",
        },
        {
          id: 8,
          title: "Optimización SEO",
          description: "Mejoramos tu visibilidad en los motores de búsqueda.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743197/seoservice_lea6ei.webp",
          imagePosition: "46% center",
        },
        {
          id: 9,
          title: "Consultoría IT",
          description: "Asesoramiento especializado en transformación digital.",
          image:
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743192/asessoryservice_u0mouv.webp",
          imagePosition: "60% center",
        },
      ],
    ],
    [],
  );

  const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0);
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(
    null,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);

  // ── Mobile horizontal carousel state (same pattern as ConstructionSection) ───
  const [mobileCenter, setMobileCenter] = useState(0);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [mobileAnimating, setMobileAnimating] = useState(false);
  const mobileOffsetRef = useRef(0);

  // 5-window of groups centered around mobileCenter (indices wrap around)
  const mobileWindowed = useMemo(() => {
    const N = serviceGroups.length;
    const idx = (n: number) => ((n % N) + N) % N;
    return [
      serviceGroups[idx(mobileCenter - 2)],
      serviceGroups[idx(mobileCenter - 1)],
      serviceGroups[mobileCenter],
      serviceGroups[idx(mobileCenter + 1)],
      serviceGroups[idx(mobileCenter + 2)],
    ];
  }, [mobileCenter, serviceGroups]);

  const mobileVisualCenter = 2 + mobileOffset;
  const mobileTranslateX = `calc(50% - ${
    mobileVisualCenter * MOBILE_STEP + MOBILE_COL_W / 2
  }px)`;

  const goMobileNext = () => {
    if (mobileAnimating) return;
    setMobileAnimating(true);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        mobileOffsetRef.current = 1;
        setMobileOffset(1);
      }),
    );
  };

  const goMobilePrev = () => {
    if (mobileAnimating) return;
    setMobileAnimating(true);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        mobileOffsetRef.current = -1;
        setMobileOffset(-1);
      }),
    );
  };

  const onMobileTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName !== "transform") return;
      const dir = mobileOffsetRef.current;
      mobileOffsetRef.current = 0;
      const N = serviceGroups.length;
      setMobileCenter((prev) =>
        dir === 1 ? (prev + 1) % N : dir === -1 ? (prev - 1 + N) % N : prev,
      );
      setMobileOffset(0);
      setMobileAnimating(false);
    },
    [serviceGroups.length],
  );

  // ── Desktop carousel state ────────────────────────────────────────────────────
  const next = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 700);
    setCurrentGroupIndex((prev) =>
      prev === serviceGroups.length - 1 ? 0 : prev + 1,
    );
  };

  const prev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 700);
    setCurrentGroupIndex((prev) =>
      prev === 0 ? serviceGroups.length - 1 : prev - 1,
    );
  };

  const activeGroupRef = useRef<HTMLDivElement | null>(null);
  const [activeGroupHeight, setActiveGroupHeight] = useState<number>(0);

  const measureActive = useCallback(() => {
    const el = activeGroupRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setActiveGroupHeight(rect.height || 0);
  }, []);

  useLayoutEffect(() => {
    if (isMobile) return;
    const el = activeGroupRef.current;
    if (!el) return;
    measureActive();
    const ro = new ResizeObserver(() => measureActive());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile, currentGroupIndex, measureActive]);

  useEffect(() => {
    if (isMobile) return;
    const onResize = () => measureActive();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile, measureActive]);

  const arrowOffset = Math.max(0, activeGroupHeight / 2 + 60);

  return (
    <section
      ref={sectionRef}
      className="lg:min-h-screen bg-black text-white py-10 md:py-14 lg:py-16 lg:px-20 relative overflow-hidden isolate"
    >
      {/* Oscurece bordes */}
      <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-b from-black via-black to-transparent h-[36%]" />
      <div className="absolute top-[74%] inset-0 z-20 pointer-events-none bg-gradient-to-t from-black via-black to-transparent h-[36%]" />

      {/* Brillo central */}
      {/* Brillo central con efecto de respiración */}
      <div
        className="absolute inset-0 pointer-events-none breathing-glow bg-[radial-gradient(ellipse_85%_115%_at_50%_50%,#D08E00_18%,#6A4800_32%,#000000_55%)] md:bg-[radial-gradient(ellipse_40%_110%_at_50%_50%,#D08E00_18%,#6A4800_32%,#000000_50%)]"
        style={{
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 14%, rgba(0,0,0,1) 86%, rgba(0,0,0,0) 100%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 14%, rgba(0,0,0,1) 86%, rgba(0,0,0,0) 100%)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
        }}
      />

      <div className="text-center relative z-40 px-4 mt-10">
        <h2 className="text-xl md:text-2xl font-bold tracking-[4px] mb-8">
          CONOCE NUESTROS SERVICIOS
        </h2>
      </div>

      <div className="relative h-[78vh] sm:h-[85vh] lg:h-[100vh] w-full mx-auto flex items-center justify-center z-60">
        <div className="relative h-full w-full overflow-hidden flex items-center justify-center">
          {isMobile ? (
            <div className="relative w-full">
              {/* Left arrow */}
              <button
                onClick={goMobilePrev}
                disabled={mobileAnimating}
                className="
                  absolute left-2 top-1/2 -translate-y-1/2 z-20
                  w-10 h-10 rounded-full
                  bg-gradient-to-r from-prin5 to-prin4
                  flex items-center justify-center
                  transition-all duration-300 shadow-lg
                  disabled:opacity-60
                "
                aria-label="Anterior"
              >
                <ChevronLeft className="text-white w-5 h-5" />
              </button>

              {/* Clipping viewport */}
              <div className="overflow-hidden relative">
                {/* The sliding strip — 5 columns */}
                <div
                  className="flex items-start"
                  style={{
                    gap: `${MOBILE_GAP}px`,
                    transform: `translateX(${mobileTranslateX})`,
                    transition: mobileAnimating
                      ? `transform ${MOBILE_DURATION} ${MOBILE_EASING}`
                      : "none",
                  }}
                  onTransitionEnd={onMobileTransitionEnd}
                >
                  {mobileWindowed.map((group, colIdx) => {
                    const dist = Math.abs(colIdx - mobileVisualCenter);
                    const { opacity, scale } = distStyle(dist);

                    return (
                      <div
                        key={colIdx}
                        style={{
                          width: `${MOBILE_COL_W}px`,
                          flexShrink: 0,
                          opacity,
                          transform: `scale(${scale})`,
                          transition: mobileAnimating
                            ? `opacity ${MOBILE_DURATION} ${MOBILE_EASING}, transform ${MOBILE_DURATION} ${MOBILE_EASING}`
                            : "none",
                        }}
                        className="flex flex-col gap-3 items-center"
                      >
                        {group.map((service) => (
                          <button
                            key={service.id}
                            onClick={() =>
                              dist === 0 && setSelectedService(service)
                            }
                            className="flex flex-col items-center w-full text-left"
                          >
                            <div
                              className="relative overflow-hidden rounded-[20px] border border-white/10 w-full shrink-0"
                              style={{ height: `${MOBILE_CARD_H}px` }}
                            >
                              <img
                                loading="lazy"
                                decoding="async"
                                src={service.image}
                                alt={service.title}
                                className="w-full h-full object-cover scale-[1.06]"
                                style={{
                                  objectPosition:
                                    service.imagePosition ?? "center",
                                }}
                              />

                              <div className="absolute inset-x-0 bottom-0 backdrop-blur-sm bg-gradient-to-r from-prin5/20 to-gold/50 text-white p-2">
                                <h3 className="text-[10px] font-bold uppercase tracking-wider leading-tight pr-6">
                                  {service.title}
                                </h3>
                                <div className="absolute bottom-2 right-2">
                                  <ArrowRight className="text-white w-3 h-3" />
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Fade edges */}
                <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent z-10" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black to-transparent z-10" />
              </div>

              {/* Right arrow */}
              <button
                onClick={goMobileNext}
                disabled={mobileAnimating}
                className="
                  absolute right-2 top-1/2 -translate-y-1/2 z-20
                  w-10 h-10 rounded-full
                  bg-gradient-to-l from-prin5 to-prin4
                  flex items-center justify-center
                  transition-all duration-300 shadow-lg
                  disabled:opacity-60
                "
                aria-label="Siguiente"
              >
                <ChevronRight className="text-white w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              {serviceGroups.map((group, groupIndex) => {
                const offset = groupIndex - currentGroupIndex;
                const rawOffset =
                  offset < -Math.floor(serviceGroups.length / 2)
                    ? offset + serviceGroups.length
                    : offset > Math.floor(serviceGroups.length / 2)
                      ? offset - serviceGroups.length
                      : offset;

                const isActive = rawOffset === 0;
                const isAdjacent = Math.abs(rawOffset) === 1;
                const isFar = Math.abs(rawOffset) > 1;

                const translateY = rawOffset * 430;
                const groupOpacity = isActive ? 1 : isAdjacent ? 0.75 : 0;

                const fadeDirection = rawOffset < 0 ? "to bottom" : "to top";
                const maskValue = !isActive
                  ? `linear-gradient(${fadeDirection}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,1) 100%)`
                  : "none";

                return (
                  <div
                    key={groupIndex}
                    ref={isActive ? activeGroupRef : null}
                    className="absolute left-1/2 w-full max-w-[75vw]"
                    style={{
                      top: "50%",
                      transform: `translate(-50%, calc(-50% + ${translateY}px)) scale(${
                        isActive ? 1 : 0.96
                      })`,
                      opacity: groupOpacity,
                      visibility: isFar ? "hidden" : "visible",
                      zIndex: isActive ? 40 : 0,
                      pointerEvents: isActive ? "auto" : "none",
                      transition:
                        "transform 700ms cubic-bezier(0.4, 0, 0.2, 1), opacity 700ms ease",
                      WebkitMaskImage:
                        maskValue as React.CSSProperties["WebkitMaskImage"],
                      WebkitMaskRepeat: "no-repeat",
                      WebkitMaskSize: "100% 100%",
                      maskImage: maskValue,
                      maskRepeat: "no-repeat",
                      maskSize: "100% 100%",
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 place-items-center px-4">
                      {group.map((service) => (
                        <div
                          key={service.id}
                          onClick={() =>
                            isActive && setSelectedService(service)
                          }
                          className={`
                            relative my-2 md:my-0 overflow-hidden transition-all duration-300
                            rounded-lg md:rounded-[30px] lg:rounded-[50px]
                            w-[400px] h-[130px]
                            md:w-[22vw] md:h-[300px]
                            lg:w-[22vw] lg:max-w-[300px] lg:h-[400px]
                            ${
                              isActive
                                ? "cursor-pointer border border-white/10"
                                : "cursor-default"
                            }
                          `}
                        >
                          <img
                            loading="lazy"
                            decoding="async"
                            src={service.image}
                            alt={service.title}
                            className={`w-full h-full object-cover scale-[1.2] transition-all duration-300 ${
                              isActive ? "" : "blur-sm brightness-90"
                            }`}
                            style={{
                              objectPosition: service.imagePosition ?? "center",
                            }}
                          />

                          <div className="absolute bottom-0 left-0 right-0 backdrop-blur-sm bg-gradient-to-r from-prin5/20 to-gold/50 text-white p-4 z-20">
                            <h3 className="text-lg font-bold mb-1 ml-0 xl:px-4 pr-12">
                              {service.title}
                            </h3>
                            <p className="text-sm line-clamp-2 ml-0 xl:ml-4 pr-12">
                              {service.description}
                            </p>
                            <div className="absolute bottom-4 right-4 p-2 rounded-full transition-colors duration-300">
                              <ArrowRight className="text-white" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {!selectedService && (
                <div className="absolute inset-0 z-50 pointer-events-none">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prev();
                    }}
                    className="
                      pointer-events-auto
                      absolute left-1/2 top-1/2 -translate-x-1/2
                      bg-gradient-to-b from-prin5 to-prin4 px-6 py-3 rounded-[22px]
                      transition-all duration-300 shadow-lg
                    "
                    style={{
                      transform: `translate(-50%, calc(-50% - ${arrowOffset}px))`,
                    }}
                    aria-label="Anterior"
                  >
                    <ChevronUp className="text-white text-2xl" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      next();
                    }}
                    className="
                      pointer-events-auto
                      absolute left-1/2 top-1/2 -translate-x-1/2
                      bg-gradient-to-t from-prin5 to-prin4 px-6 py-3 rounded-[22px]
                      transition-all duration-300 shadow-lg
                    "
                    style={{
                      transform: `translate(-50%, calc(-50% + ${arrowOffset}px))`,
                    }}
                    aria-label="Siguiente"
                  >
                    <ChevronDown className="text-white text-2xl" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* MODAL */}
      {selectedService && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Servicio"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onMouseDown={() => setSelectedService(null)}
          />

          <div
            className="relative w-full max-w-3xl rounded-2xl p-[1px] shadow-2xl max-h-[90vh]"
            style={{
              background:
                "linear-gradient(90deg, #7F5600 0%, #463002 23%, #261A00 100%)",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className="
                w-full max-h-[90vh] overflow-hidden rounded-2xl
                bg-gradient-to-r from-[#000000] to-[#201600]
                flex flex-col
              "
            >
              <div className="flex items-start justify-between gap-4 p-5 sm:p-6 shrink-0">
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold leading-snug">
                    {selectedService.title}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedService(null)}
                  className="shrink-0 rounded-full p-2 hover:bg-white/10 transition"
                  aria-label="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="px-5 sm:px-6 pb-5 sm:pb-6 overflow-y-auto">
                <img
                  loading="lazy"
                  decoding="async"
                  src={selectedService.image}
                  alt={selectedService.title}
                  className="
                    w-full aspect-video object-cover object-center
                    rounded-lg mb-4
                    max-h-[48vh]
                  "
                />

                <p className="text-gray-200 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                  {selectedService.description}
                </p>
              </div>
            </div>
          </div>

          <KeyListener onEscape={() => setSelectedService(null)} />
        </div>
      )}
    </section>
  );
};

export default ServicesView;

const KeyListener: React.FC<{ onEscape: () => void }> = ({ onEscape }) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onEscape]);

  return null;
};
